/**
 * YearEndPromptModal
 *
 * 연말정산 미실시 유저에게 참여를 유도하는 모달
 * 디자인 시스템: MUI Dialog + 기존 컴포넌트 재사용
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stack,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { IconSparkles } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../hooks/useIsMobile';
import LoopVideoBlock from '../video/LoopVideoBlock';

// 연말정산 영상 URL (YearEndHeroSection, YearEndBanner와 동일)
const YEAR_REVIEW_VIDEO = 'https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/yearEnd%2F2025%2Fyear_review.mp4?alt=media';

/**
 * YearEndPromptModal
 *
 * @param {boolean} open - 모달 열림 상태
 * @param {function} onClose - 닫기 핸들러 (나중에)
 * @param {function} onPermanentDismiss - 영구 닫기 핸들러 (다시 보지 않기)
 */
export const YearEndPromptModal = ({
  open,
  onClose,
  onPermanentDismiss,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile('sm');
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleNavigate = () => {
    // 다시 보지 않기 체크 시 영구 닫기
    if (dontShowAgain && onPermanentDismiss) {
      onPermanentDismiss();
    } else if (onClose) {
      onClose();
    }
    navigate('/year-end');
  };

  const handleClose = () => {
    if (dontShowAgain && onPermanentDismiss) {
      onPermanentDismiss();
    } else if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          overflow: 'hidden',
          maxWidth: isMobile ? 'calc(100% - 32px)' : '480px',
          m: isMobile ? 2 : 3,
        },
      }}
    >
      {/* 영상 영역 */}
      <Box
        sx={{
          width: '100%',
          height: isMobile ? '180px' : '220px',
          overflow: 'hidden',
        }}
      >
        <LoopVideoBlock
          videoUrl={YEAR_REVIEW_VIDEO}
          isMobile={isMobile}
          width="100%"
          height="100%"
          enableHover={false}
          playOnHover={false}
          borderRadius={0}
        />
      </Box>

      <DialogContent sx={{ p: isMobile ? 3 : 4 }}>
        <Stack spacing={3}>
          {/* 타이틀 & 설명 */}
          <Stack spacing={1} textAlign="center">
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              fontWeight={800}
              color="text.primary"
            >
              {t('yearEndPrompt.title', '2025 연말정산을 만들어보세요!')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.6 }}
            >
              {t('yearEndPrompt.description', '올해의 스레드 활동을 정리하고 친구들과 공유하세요.')}
            </Typography>
          </Stack>

          {/* CTA 버튼 */}
          <Stack spacing={1.5}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<IconSparkles size={20} />}
              onClick={handleNavigate}
              sx={{
                py: 1.5,
                fontWeight: 700,
                borderRadius: '12px',
                textTransform: 'none',
              }}
            >
              {t('yearEndPrompt.cta', '연말정산 만들기')}
            </Button>

            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={handleClose}
              sx={{
                py: 1.5,
                fontWeight: 600,
                borderRadius: '12px',
                textTransform: 'none',
              }}
            >
              {t('yearEndPrompt.later', '나중에')}
            </Button>
          </Stack>

          {/* 다시 보지 않기 체크박스 */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  {t('yearEndPrompt.dontShowAgain', '다시 보지 않기')}
                </Typography>
              }
            />
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default YearEndPromptModal;
